import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { StarIcon as SolidStarIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { StarIcon as OutlineStarIcon, MagnifyingGlassIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { UserContext } from "../App";
import { useHeader } from "../components/Layout";
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import Spinner from '../components/MatrixLoader';
import ZoomableImageModal from '../components/ZoomableImageModal';
import DayScoreCardModal from "../components/DayScoreCardModal";
import { getCreatedDate } from "../utils/dateUtils";
import { isTapeReading } from '../utils/tradeHelpers';
import { useConfirm, useToast, SegmentedControl, Select } from '../components/ui';
import { DEFAULT_BE_THRESHOLD, sanitizeBeThreshold } from '../statsUtils';
import {
  TRADE_OUTCOME_OPTIONS,
  TRADE_SORT_OPTIONS,
  filterAndSortTradeGroups,
  buildViewerItems,
  viewerItemKey,
  stepIndex,
  formatEntryDate,
  formatSessionModel,
} from '../utils/tradesView';

const parseCreatedDate = (entry) => {
  if (entry.created) {
    const d = getCreatedDate(entry.created);
    if (d) return d;
  }
  // Fallback to constructed date from year/month/day fields
  if (entry.year && entry.month && entry.day) {
    return new Date(entry.year, entry.month - 1, entry.day);
  }
  return new Date(); // Last resort
};

const hasCardPnl = (entry) =>
  !entry.isDeposit && !entry.isPayout && entry.pnl !== undefined && entry.pnl !== null && entry.pnl !== '';

// Title, date, P&L and account lines shared by every trade card.
const TradeCardMeta = ({ entry, className = 'mt-auto pt-1.5 text-center w-full' }) => {
  const sessionModel = formatSessionModel(entry);
  return (
    <div className={className}>
      {/* Title plus an optional session / entry model line, inside the same fixed-height box */}
      <div className="text-[13px] text-fg font-medium mb-0.5 h-[34px] flex flex-col items-center justify-center overflow-hidden">
        <div className="truncate max-w-full px-1 leading-tight" title={entry.title || ''}>
          {entry.title || ''}
        </div>
        {sessionModel && (
          <div className="truncate max-w-full px-1 text-[11px] font-normal leading-tight text-fg-muted" title={sessionModel}>
            {sessionModel}
          </div>
        )}
      </div>
      <div className="text-xs text-neutral-500 h-[15px] flex items-center justify-center">
        {formatEntryDate(entry)}
      </div>
      {/* Trade P&L (not shown for deposits/payouts or entries without one) */}
      {hasCardPnl(entry) && (
        <div
          className="font-mono text-sm font-bold h-[18px] flex items-center justify-center mt-0.5"
          style={{ color: Number(entry.pnl) > 0 ? 'var(--theme-success)' : Number(entry.pnl) < 0 ? 'var(--theme-loss)' : 'var(--theme-text-muted)' }}
        >
          {Number(entry.pnl) > 0 ? '+' : Number(entry.pnl) < 0 ? '-' : ''}${Math.abs(Number(entry.pnl) || 0).toFixed(2)}
        </div>
      )}
      {entry.accountName && (
        <div className="text-[11px] text-blue-400 h-[15px] flex items-center justify-center mt-0.5">
          {entry.accountName}
        </div>
      )}
    </div>
  );
};

const isTypingTarget = (target) => {
  if (!target || typeof target.closest !== 'function') return false;
  if (target.isContentEditable) return true;
  return !!target.closest('input, textarea, select, [role="dialog"], [role="alertdialog"]');
};

const TradesPage = ({ previewMode = false }) => {
  const confirm = useConfirm();
  const toast = useToast();
  const { currentUser, selectedAccount, dataRefreshTrigger } = useContext(UserContext);
  const { setShowHeader } = useHeader();
  const [loading, setLoading] = useState(!previewMode);
  const [groupedImages, setGroupedImages] = useState([]);
  // const [allEntries, setAllEntries] = useState([]); // For navigation through all entries - unused for now
  const [favorites, setFavorites] = useState({});
  // Key of the chart (or chart-less entry) open in the viewer; see viewerItemKey.
  const [viewerKey, setViewerKey] = useState(null);
  const [showTradeDetails, setShowTradeDetails] = useState(false);

  // Filter state
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString());
  const [selectedAccountFilter, setSelectedAccountFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [beThreshold, setBeThreshold] = useState(DEFAULT_BE_THRESHOLD);
  const [showFilter, setShowFilter] = useState(false);
  const [showAllTrades, setShowAllTrades] = useState(true);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [showDayCardToast, setShowDayCardToast] = useState(false);
  const [dayCardPayload, setDayCardPayload] = useState(null);
  const [isDayCardOpen, setIsDayCardOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!location.state?.showDayCardToast || !location.state?.dayCardPayload) return;
    setDayCardPayload(location.state.dayCardPayload);
    setShowDayCardToast(true);
  }, [location.state]);

  useEffect(() => {
    if (!showDayCardToast) return undefined;
    const timeoutId = setTimeout(() => setShowDayCardToast(false), 4000);
    return () => clearTimeout(timeoutId);
  }, [showDayCardToast]);

  // Load filter preferences - run on every mount to ensure preferences are restored
  useEffect(() => {
    if (!currentUser) {
      setPreferencesLoaded(false);
      return;
    }
    
    const loadFilters = async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const prefsSnap = await getDoc(prefsRef);
        
        if (prefsSnap.exists()) {
          const data = prefsSnap.data();
          if (data.tradesYear !== undefined) {
            setSelectedYear(data.tradesYear);
          }
          if (data.tradesMonth !== undefined) {
            setSelectedMonth(data.tradesMonth);
          }
          if (data.tradesAccountFilter !== undefined) {
            setSelectedAccountFilter(data.tradesAccountFilter);
          }
          // Read-only: same break-even rule Summary and Calendar use.
          setBeThreshold(sanitizeBeThreshold(data.beThreshold));
        }
        // Mark preferences as loaded after attempting to load (even if none exist)
        setPreferencesLoaded(true);
      } catch (error) {
        console.error('Error loading trades filters:', error);
        // Still mark as loaded to prevent infinite waiting
        setPreferencesLoaded(true);
      }
    };
    
    loadFilters();
  }, [currentUser]); // Keep currentUser dependency, but this will run on mount when currentUser exists

  // Save filter preferences when they change - only save after preferences have been loaded
  useEffect(() => {
    if (!currentUser || !selectedYear || !selectedMonth || !preferencesLoaded) return;
    
    const saveFilters = async () => {
      try {
        const { doc, setDoc } = await import('firebase/firestore');
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        await setDoc(prefsRef, { 
          tradesYear: selectedYear,
          tradesMonth: selectedMonth,
          tradesAccountFilter: selectedAccountFilter
        }, { merge: true });
      } catch (error) {
        console.error('Error saving trades filters:', error);
      }
    };
    
    saveFilters();
  }, [currentUser, selectedYear, selectedMonth, selectedAccountFilter, preferencesLoaded]);

  // Function to delete a trade from all accounts and orphaned entries
  const deleteTrade = async (entry) => {
    if (!currentUser || !entry) return;
    
    try {
      // If entry is orphaned, delete from orphanedEntries collection
      if (entry.isOrphaned) {
        const orphanedDoc = doc(db, 'users', currentUser.uid, 'orphanedEntries', entry.id);
        await deleteDoc(orphanedDoc);
      } else {
        // Get all accounts for the current user
        const accountsCol = collection(db, 'users', currentUser.uid, 'accounts');
        const accountsSnap = await getDocs(accountsCol);
        const accounts = accountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Delete the entry from all accounts where it exists
        const deletePromises = accounts.map(async (account) => {
          const entryDoc = doc(db, 'users', currentUser.uid, 'accounts', account.id, 'entries', entry.id);
          try {
            await deleteDoc(entryDoc);
          } catch (error) {
            // Entry might not exist in this account, that's okay
          }
        });
        
        await Promise.all(deletePromises);
      }
      
      // Update local state instead of refreshing the page
      setGroupedImages(prev => prev.filter(group => group.entry.id !== entry.id));
      // setAllEntries removed - state is commented out and not used
      
      // Close any open modals if the deleted entry was being viewed
      if (tradeEntry && tradeEntry.id === entry.id) {
        closeImageViewer();
      }
    } catch (error) {
      console.error('Error deleting trade:', error?.message);
      toast.error('Could not delete the trade. Try again.');
    }
  };

  const confirmDeleteTrade = async (entry) => {
    if (!entry) return;

    const fallbackLabel = entry.isDeposit
      ? 'this deposit'
      : entry.isPayout
      ? 'this payout'
      : entry.tapeReading
      ? 'this tape reading'
      : 'this trade';
    const label = entry.title?.trim() ? `"${entry.title.trim()}"` : fallbackLabel;
    const scopeMessage = entry.isOrphaned
      ? 'This action cannot be undone.'
      : 'This action cannot be undone and will delete it from all accounts.';

    const ok = await confirm({
      tone: 'danger',
      title: `Delete ${label}?`,
      description: scopeMessage,
      confirmLabel: 'Delete',
    });
    if (ok) deleteTrade(entry);
  };

  useEffect(() => {
    if (!currentUser || !selectedAccount) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        // Using static imports from the top of the file
        
        // TRADES & TAPE READINGS: Fetch from ALL accounts (cross-account)
        let allTradesAndTapeReadings = [];
        
        // Get all accounts for the current user
        const accountsCol = collection(db, 'users', currentUser.uid, 'accounts');
        const accountsSnap = await getDocs(accountsCol);
        const accounts = accountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Fetch trades and tape readings from each account in parallel
        const accountFetchPromises = accounts.map(async (account) => {
          const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', account.id, 'entries');
          const snap = await getDocs(entriesCol);
          return snap.docs
            .map(doc => ({ 
              ...doc.data(), 
              id: doc.id,
              accountId: account.id,
              accountName: account.name
            }))
            .filter(entry => !entry.isDeposit && !entry.isPayout && !entry.isBalanceAdjustment); // Only trades and tape readings
        });
        
        const accountEntriesArrays = await Promise.all(accountFetchPromises);
        allTradesAndTapeReadings = accountEntriesArrays.flat();
      
        // Fetch orphaned entries (from deleted accounts)
        try {
          const orphanedCol = collection(db, 'users', currentUser.uid, 'orphanedEntries');
          const orphanedSnap = await getDocs(orphanedCol);
          const orphanedEntries = orphanedSnap.docs
            .map(doc => ({ 
              ...doc.data(), 
              id: doc.id,
              accountId: doc.data().originalAccountId || 'deleted',
              accountName: doc.data().originalAccountName || 'Deleted Account',
              isOrphaned: true
            }))
            .filter(entry => !entry.isDeposit && !entry.isPayout && !entry.isBalanceAdjustment); // Only trades and tape readings
          allTradesAndTapeReadings.push(...orphanedEntries);
        } catch (error) {
          // Silently handle missing orphaned entries
        }
      
        // DEPOSITS & PAYOUTS: Fetch from ONLY the selected account (account-specific)
        let selectedAccountDepositsPayouts = [];
        if (selectedAccount) {
          const selectedEntriesCol = collection(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries');
          const selectedSnap = await getDocs(selectedEntriesCol);
          selectedAccountDepositsPayouts = selectedSnap.docs
            .map(doc => ({ 
              ...doc.data(), 
              id: doc.id,
              accountId: selectedAccount.id,
              accountName: selectedAccount.name
            }))
            .filter(entry => entry.isDeposit || entry.isPayout); // Only deposits and payouts
        }
        
        // Combine all entries
        const allEntries = [...allTradesAndTapeReadings, ...selectedAccountDepositsPayouts];
        
        // Helper function to clean timestamps
        const getCleanTimestamp = (created) => {
          if (!created) return '';
          const lastDashIndex = created.lastIndexOf('-');
          return lastDashIndex > 10 ? created.substring(0, lastDashIndex) : created;
        };
        
        // Sort all entries by creation date (newest first) - only once
        allEntries.sort((a, b) => {
          const cleanA = getCleanTimestamp(a.created);
          const cleanB = getCleanTimestamp(b.created);
          return new Date(cleanB) - new Date(cleanA);
        });
        
        // Fetch favorites (use a shared favorites collection for all accounts)
        const favDoc = await getDoc(doc(db, 'favorites', currentUser.uid + '_shared'));
        setFavorites(favDoc.exists() ? favDoc.data().data : {});
        
        // Build grouped images array
        const grouped = [];
        const getDayLink = (entry) => {
          if (entry.year && entry.month && entry.day) {
            return `/day/${entry.year}/${entry.month}/${entry.day}`;
          }
          if (entry.month && entry.day && entry.created) {
            const clean = getCleanTimestamp(entry.created);
            const parsed = new Date(clean);
            if (!Number.isNaN(parsed.getTime())) {
              return `/day/${parsed.getFullYear()}/${entry.month}/${entry.day}`;
            }
          }
          if (entry.month && entry.day) {
            return `/day/${entry.month}/${entry.day}`;
          }
          return '#';
        };
        
        allEntries.forEach(entry => {
          // Normalize image data - handle different ways images might be stored
          let imageArray = [];
          
          if (entry.screenshots && entry.screenshots.length) {
            imageArray = [...entry.screenshots];
          } else if (entry.images && entry.images.length) {
            imageArray = [...entry.images];
          } else if (entry.imageUrls && entry.imageUrls.length) {
            imageArray = [...entry.imageUrls];
          } else if (entry.image) {
            imageArray = [entry.image];
          } else if (entry.screenshot) {
            imageArray = [entry.screenshot];
          } else if (entry.imageUrl) {
            imageArray = [entry.imageUrl];
          }
          
          // For entries with images (any format)
          if (imageArray.length > 0) {
            const entryImages = imageArray.map((src, imageIndex) => ({
              src,
              created: entry.created,
              entry: entry,
              link: getDayLink(entry),
              imageIndex: imageIndex, // Track which image within the entry
              isIndividualImage: true
            }));
            
            grouped.push({
              entry: entry,
              images: entryImages,
              totalImages: entryImages.length,
              firstImage: entryImages[0]
            });
            
          }
          // For deposits and payouts (create placeholder)
          else if (entry.isDeposit || entry.isPayout) {
            grouped.push({
              entry: entry,
              images: [],
              totalImages: 0,
              isDeposit: entry.isDeposit,
              isPayout: entry.isPayout
            });
            
          }
          // For trades/tape readings without any images (still show them with placeholder)
          else if (!entry.isDeposit && !entry.isPayout) {
            grouped.push({
              entry: entry,
              images: [],
              totalImages: 0,
              isTrade: !isTapeReading(entry),
              isTapeReading: isTapeReading(entry)
            });
            
          }
        });
        
        // Sort grouped by creation date (newest first) - using same helper
        grouped.sort((a, b) => {
          const cleanA = getCleanTimestamp(a.entry.created);
          const cleanB = getCleanTimestamp(b.entry.created);
          return new Date(cleanB) - new Date(cleanA);
        });
        
        setGroupedImages(grouped);
        // setAllEntries removed - state is commented out and not used
      } catch (error) {
        console.error('Error fetching trades data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentUser, selectedAccount, dataRefreshTrigger]); // Include selectedAccount since deposits/payouts are account-specific



  // Reset filters and ensure header is shown when component unmounts
  useEffect(() => {
    return () => {
      setShowHeader(true);
    };
  }, [setShowHeader]);

  const toggleFavorite = async (src) => {
    const updated = { ...favorites, [src]: !favorites[src] };
    // Sanitize: only keep keys with boolean values
    const sanitized = Object.fromEntries(
      Object.entries(updated).filter(([k, v]) => typeof v === "boolean")
    );
    const previous = favorites;
    setFavorites(sanitized);
    try {
      await setDoc(doc(db, 'favorites', currentUser.uid + '_shared'), { data: sanitized });
    } catch (error) {
      // Roll back so the star matches what is saved.
      console.error('Error saving favorite:', error?.message);
      setFavorites(previous);
      toast.error('Could not update favorites. Try again.');
    }
  };

  // Month/year + account filter (unchanged behaviour), before search/outcome/sort.
  const dateAccountGroups = useMemo(() => {
    const accountMatches = (group) => {
      if (selectedAccountFilter !== 'current') return true;
      // If current account is selected but no account is available, show nothing
      if (!selectedAccount || !selectedAccount.id) return false;
      return !group.entry || group.entry.accountId === selectedAccount.id;
    };

    // If showAllTrades is enabled, show all trades regardless of date (account filter still applies)
    if (showAllTrades) {
      return selectedAccountFilter === 'current' && selectedAccount && selectedAccount.id
        ? groupedImages.filter(accountMatches)
        : groupedImages;
    }

    return groupedImages.filter(group => {
      if (!accountMatches(group)) return false;

      // First try to use the month/day/year fields (primary approach)
      if (group.entry.month && group.entry.day) {
        const entryYear = group.entry.year || new Date().getFullYear().toString();
        return (
          Number(entryYear) === Number(selectedYear) &&
          Number(group.entry.month) === Number(selectedMonth)
        );
      }

      // Fallback: parse from created timestamp ("2025-01-06T03:43:47.677Z-u38k53")
      if (typeof group.entry.created === 'string') {
        const lastDashIndex = group.entry.created.lastIndexOf('-');
        if (lastDashIndex > 10) { // Make sure it's not a date dash
          const date = new Date(group.entry.created.substring(0, lastDashIndex));
          if (!isNaN(date.getTime())) {
            return (
              date.getFullYear().toString() === selectedYear &&
              (date.getMonth() + 1).toString() === selectedMonth
            );
          }
        }
      }

      // If we can't determine the date, exclude from month/year filtered view.
      return false;
    });
  }, [groupedImages, selectedYear, selectedMonth, showAllTrades, selectedAccountFilter, selectedAccount]);

  // Search + outcome + sort on top of the date/account filter.
  const filteredGroups = useMemo(
    () => filterAndSortTradeGroups(dateAccountGroups, {
      query: searchQuery,
      outcome: outcomeFilter,
      sort: sortOrder,
      beThreshold,
    }),
    [dateAccountGroups, searchQuery, outcomeFilter, sortOrder, beThreshold]
  );

  const hasRefinement = searchQuery.trim() !== '' || outcomeFilter !== 'all';
  const panelFiltersActive = outcomeFilter !== 'all' || sortOrder !== 'newest' || !showAllTrades || selectedAccountFilter === 'current';
  const clearRefinements = () => {
    setSearchQuery('');
    setOutcomeFilter('all');
  };

  // Viewer steps through exactly what the grid shows, in the same order.
  const viewerItems = useMemo(() => buildViewerItems(filteredGroups), [filteredGroups]);
  const viewerIndex = viewerKey ? viewerItems.findIndex((item) => item.key === viewerKey) : -1;
  const selectedImage = viewerIndex >= 0 ? viewerItems[viewerIndex] : null;
  const tradeEntry = selectedImage ? selectedImage.entry : null;

  const openImageViewer = (image, entry) => {
    setViewerKey(viewerItemKey(entry, image?.imageIndex ?? 0));
    setShowHeader(false);
  };

  // Deposits/payouts/trades without screenshots
  const openEntryDetails = (entry) => {
    setViewerKey(viewerItemKey(entry, null));
    setShowHeader(false);
  };

  const closeImageViewer = useCallback(() => {
    setViewerKey(null);
    setShowTradeDetails(false);
    setShowHeader(true);
  }, [setShowHeader]);

  // The open item disappeared (deleted, or data refreshed without it): close the viewer.
  useEffect(() => {
    if (viewerKey && viewerIndex < 0) closeImageViewer();
  }, [viewerKey, viewerIndex, closeImageViewer]);

  const navigateViewer = useCallback((direction) => {
    const nextIdx = stepIndex(viewerItems.length, viewerIndex, direction);
    if (nextIdx < 0) return;
    setViewerKey(viewerItems[nextIdx].key);
  }, [viewerItems, viewerIndex]);

  const handleKeyDown = useCallback((e) => {
    if (!viewerKey || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    // Leave keys alone while a confirm dialog or a field has focus.
    if (isTypingTarget(e.target)) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeImageViewer();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      navigateViewer('prev');
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      navigateViewer('next');
    }
  }, [viewerKey, closeImageViewer, navigateViewer]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Month names for the picker
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun", 
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const yearOptions = [];
  for (let y = currentYear - 3; y <= currentYear + 2; y++) yearOptions.push(y);

  return (
    <div className="page-shell relative animate-fadeIn" style={{ zIndex: 0 }}>
      {/* Page actions: one slim row (search, match count, filter button) */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" aria-hidden="true" />
          <input
            type="text"
            inputMode="search"
            enterKeyHint="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape' && searchQuery) { e.stopPropagation(); setSearchQuery(''); } }}
            placeholder="Search trades"
            aria-label="Search trades by title, ticker or notes"
            className="h-11 w-full rounded-lg border border-line bg-surface-2 pl-9 pr-8 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-fg-muted hover:text-fg"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {!loading && groupedImages.length > 0 && (
          <span className="ml-auto whitespace-nowrap text-xs tabular-nums text-fg-muted" aria-live="polite">
            {hasRefinement
              ? `${filteredGroups.length} of ${dateAccountGroups.length}`
              : `${filteredGroups.length} ${filteredGroups.length === 1 ? 'trade' : 'trades'}`}
          </span>
        )}
        <button
          type="button"
          onClick={() => setShowFilter(!showFilter)}
          className={`relative shrink-0 bg-[#6B7280] hover:bg-[#9CA3AF] text-white p-3 rounded-lg transition-all ${!loading && groupedImages.length > 0 ? '' : 'ml-auto'}`}
          aria-label={panelFiltersActive ? 'Filter trades (filters active)' : 'Filter trades'}
          aria-expanded={showFilter}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
          </svg>
          {panelFiltersActive && (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#3B82F6] ring-2 ring-[#6B7280]" aria-hidden="true" />
          )}
        </button>
      </div>
      
      {/* Filter Panel */}
      <AnimatePresence>
        {showFilter && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white dark:bg-[#1F2937] rounded-xl p-6 mb-6 border border-[#E5E7EB] dark:border-[#374151] shadow-subtle"
          >
            {/* Outcome + sort: one compact row */}
            <div className="flex flex-wrap items-start gap-x-3 sm:gap-x-6 gap-y-4 mb-6">
              <div>
                <span className="block text-[#374151] dark:text-[#9CA3AF] mb-2 font-medium">Outcome</span>
                <SegmentedControl
                  label="Outcome"
                  size="sm"
                  options={TRADE_OUTCOME_OPTIONS}
                  value={outcomeFilter}
                  onChange={setOutcomeFilter}
                />
              </div>
              <div className="w-32 sm:w-40">
                <label htmlFor="trades-sort" className="block text-[#374151] dark:text-[#9CA3AF] mb-2 font-medium">Sort</label>
                <Select id="trades-sort" size="sm" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                  {TRADE_SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>
              </div>
              {outcomeFilter !== 'all' && (
                <p className="text-xs text-fg-muted self-end pb-2">
                  Break-even is within ±${beThreshold}, same as Summary and Calendar.
                </p>
              )}
            </div>
            <div className="grid gap-6 lg:grid-cols-[minmax(180px,220px)_minmax(0,1fr)_minmax(280px,340px)] lg:gap-x-8 items-start">
              {/* Year Input */}
              <div className="w-full">
                <label className="block text-[#374151] dark:text-[#9CA3AF] mb-2 font-medium">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] border border-[#E5E7EB] dark:border-[#374151] rounded-lg px-4 py-3 text-lg focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6]"
                >
                  {yearOptions.map(year => (
                    <option key={year} value={year.toString()}>{year}</option>
                  ))}
                </select>
              </div>
              
              {/* Month Picker */}
              <div className="w-full">
                <label className="block text-[#374151] dark:text-[#9CA3AF] mb-2 font-medium">Month</label>
                <div className="flex flex-wrap gap-2 sm:gap-3 max-w-[28rem]">
                  {monthNames.map((month, index) => (
                    <motion.button
                      key={index}
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelectedMonth((index + 1).toString())}
                      className={`rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center text-sm sm:text-lg font-medium transition-all ${
                        selectedMonth === (index + 1).toString()
                          ? 'bg-[#3B82F6] text-white shadow-lg'
                          : 'bg-[#F3F4F6] dark:bg-[#374151] text-[#374151] dark:text-[#E5E7EB] hover:bg-[#E5E7EB] dark:hover:bg-[#4B5563] border border-[#E5E7EB] dark:border-[#374151]'
                      }`}
                    >
                      {month}
                    </motion.button>
                  ))}
                </div>
              </div>
              
              <div className="w-full space-y-6">
                {/* Account Filter Toggle */}
                <div>
                  <label className="block text-[#374151] dark:text-[#9CA3AF] mb-2 font-medium">Account</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedAccountFilter('all')}
                      className={`px-4 py-3 rounded-lg font-medium transition-all ${
                        selectedAccountFilter === 'all'
                          ? 'bg-[#3B82F6] text-white shadow-lg'
                          : 'bg-[#F3F4F6] dark:bg-[#374151] text-[#374151] dark:text-[#E5E7EB] hover:bg-[#E5E7EB] dark:hover:bg-[#4B5563] border border-[#E5E7EB] dark:border-[#374151]'
                      }`}
                    >
                      All Accounts
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedAccountFilter('current')}
                      className={`px-4 py-3 rounded-lg font-medium transition-all ${
                        selectedAccountFilter === 'current'
                          ? 'bg-[#3B82F6] text-white shadow-lg'
                          : 'bg-[#F3F4F6] dark:bg-[#374151] text-[#374151] dark:text-[#E5E7EB] hover:bg-[#E5E7EB] dark:hover:bg-[#4B5563] border border-[#E5E7EB] dark:border-[#374151]'
                      }`}
                    >
                      Current Account
                    </button>
                  </div>
                </div>

                {/* Show All Trades Toggle */}
                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showAllTrades}
                      onChange={(e) => setShowAllTrades(e.target.checked)}
                      className="w-5 h-5 mt-1 text-[#3B82F6] bg-white dark:bg-[#1F2937] border-[#E5E7EB] dark:border-[#374151] rounded focus:ring-[#3B82F6] focus:ring-2"
                    />
                    <span className="text-[#374151] dark:text-[#9CA3AF] font-medium leading-snug">
                      Show All Trades (Ignore Date Filter)
                    </span>
                  </label>
                  {showAllTrades && (
                    <div className="text-sm text-[#3B82F6] dark:text-[#60A5FA] mt-2 pl-8">
                      Showing all trades regardless of month/year selection
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="max-w-full overflow-y-auto h-auto md:h-[calc(100vh-80px)]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-6 lg:gap-10 w-full pb-8"
        >
        {loading ? (
          <div className="flex flex-col items-center justify-center col-span-full py-24 min-h-[60vh]">
            <Spinner size={48} />
            <p className="text-[#6B7280] dark:text-[#9CA3AF] mt-4">Loading trades...</p>
          </div>
        ) : groupedImages.length === 0 ? (
          <div className="flex flex-col items-center justify-center col-span-full py-24 min-h-[60vh]">
              <div className="text-center space-y-4 max-w-md">
                <div className="text-6xl mb-4">📊</div>
                <div className="text-2xl font-bold text-[#111827] dark:text-[#F9FAFB] mb-2">Your trades will show here</div>
              <div className="text-lg text-[#6B7280] dark:text-[#9CA3AF]">Add your first journal entry to start building your trade history.</div>
            </div>
          </div>
        ) : filteredGroups.length === 0 && dateAccountGroups.length > 0 ? (
          <div className="flex flex-col items-center justify-center col-span-full py-24 text-center">
            <div className="text-lg font-semibold text-fg mb-1">No trades match</div>
            <div className="text-sm text-fg-muted mb-4">
              {searchQuery.trim() ? `Nothing found for "${searchQuery.trim()}"` : 'Try a different outcome.'}
            </div>
            <button type="button" onClick={clearRefinements} className="btn-primary">
              Clear search and outcome
            </button>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center col-span-full py-24">
            <div className="text-2xl font-bold text-[#e5e5e5] mb-2">No entries for {monthNames[parseInt(selectedMonth) - 1]} {selectedYear}</div>
            <div className="text-lg text-blue-400 mb-4">Try selecting a different month or year, or use "Show All Trades" to see all entries</div>
            <div className="flex gap-4">
              <button 
                onClick={() => {
                  setSelectedYear(new Date().getFullYear().toString());
                  setSelectedMonth((new Date().getMonth() + 1).toString());
                }}
                className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold transition-all"
              >
                Reset to Current Month
              </button>
              <button 
                onClick={() => setShowAllTrades(true)}
                className="btn-primary"
              >
                Show All Trades
              </button>
            </div>
          </div>
        ) : (
          filteredGroups.map((group, groupIdx) => (!group.isDeposit && !group.isPayout && group.totalImages === 0) ? (
          // Trade / tape reading without a chart: compact card, no empty image box
          <AnimatePresence key={viewerItemKey(group.entry, null)}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3, delay: Math.min(groupIdx, 24) * 0.03 }}
                role="button"
                tabIndex={0}
                aria-label={`Open ${group.entry.title?.trim() || (group.isTapeReading ? 'tape reading' : 'trade')} details`}
                onClick={() => openEntryDetails(group.entry)}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEntryDetails(group.entry); }
                }}
                className="bg-white dark:bg-[#1F2937]/10 backdrop-blur-lg rounded-2xl px-3 pt-2.5 pb-3 flex flex-col items-center border border-white/20 shadow-xl cursor-pointer w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6]"
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <button
                    type="button"
                    className="-ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-black/10 hover:text-fg dark:hover:bg-white/10"
                    onClick={e => { e.stopPropagation(); confirmDeleteTrade(group.entry); }}
                    aria-label={group.isTapeReading ? 'Delete tape reading' : 'Delete trade'}
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                  <span className="flex min-w-0 items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-fg-muted">
                    {group.isTapeReading && <ChartBarIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                    <span className="truncate">
                      {group.isTapeReading ? 'Tape' : (group.entry.tickerTraded || group.entry.ticker || '')}
                    </span>
                  </span>
                </div>
                <TradeCardMeta entry={group.entry} />
              </motion.div>
          </AnimatePresence>
          ) : (
          <AnimatePresence key={viewerItemKey(group.entry, null)}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3, delay: Math.min(groupIdx, 24) * 0.03 }}
                className="bg-white dark:bg-[#1F2937]/10 backdrop-blur-lg rounded-2xl px-[14px] pt-[14px] pb-3 flex flex-col items-center border border-white/20 shadow-xl cursor-pointer group w-full h-full min-h-[268px]"
              >
              {/* Deposit/Payout or Trade/TapeReading Content */}
              {group.isDeposit ? (
                // Deposit placeholder
                <div 
                  className="relative w-full aspect-square bg-black/80 rounded-xl flex items-center justify-center cursor-pointer"
                  onClick={() => openEntryDetails(group.entry)}
                >
                  <div className="text-4xl font-bold text-blue-400">$</div>
                  
                  {/* Delete Button for deposits */}
                  <button
                    className="absolute top-2 left-2 z-10"
                    onClick={e => { e.stopPropagation(); confirmDeleteTrade(group.entry); }}
                    aria-label="Delete deposit"
                    style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '9999px', padding: 3 }}
                  >
                    <motion.span
                      initial={{ scale: 0.8 }}
                      whileHover={{ scale: 1.1 }}
                      transition={{ type: 'spring', stiffness: 400 }}
                    >
                      <XMarkIcon className="w-4 h-4 text-neutral-400 dark:text-neutral-200 hover:text-neutral-300 dark:hover:text-white" />
                    </motion.span>
                  </button>
                </div>
              ) : group.isPayout ? (
                // Payout placeholder
                <div 
                  className="relative w-full aspect-square bg-black/80 rounded-xl flex items-center justify-center cursor-pointer"
                  onClick={() => openEntryDetails(group.entry)}
                >
                  <div className="theme-loss text-4xl font-bold">$</div>
                  
                  {/* Delete Button for payouts */}
                  <button
                    className="absolute top-2 left-2 z-10"
                    onClick={e => { e.stopPropagation(); confirmDeleteTrade(group.entry); }}
                    aria-label="Delete payout"
                    style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '9999px', padding: 3 }}
                  >
                    <motion.span
                      initial={{ scale: 0.8 }}
                      whileHover={{ scale: 1.1 }}
                      transition={{ type: 'spring', stiffness: 400 }}
                    >
                      <XMarkIcon className="w-4 h-4 text-neutral-400 dark:text-neutral-200 hover:text-neutral-300 dark:hover:text-white" />
                    </motion.span>
                  </button>
                </div>
              ) : group.totalImages > 0 ? (
                // Regular screenshot entry
                <>
                  {/* Favorite Star */}
                  <button
                    className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/35 backdrop-blur-sm transition-colors hover:bg-black/50"
                    onClick={e => { e.stopPropagation(); toggleFavorite(group.firstImage.src); }}
                    aria-label={favorites[group.firstImage?.src] ? 'Unfavorite' : 'Favorite'}
                  >
                    <motion.span
                      initial={{ scale: 0.9 }}
                      animate={{ scale: favorites[group.firstImage?.src] ? 1.05 : 1 }}
                      whileHover={{ scale: 1.08 }}
                      transition={{ type: 'spring', stiffness: 400 }}
                    >
                      {favorites[group.firstImage?.src] ? (
                        <SolidStarIcon className="h-4 w-4 text-[var(--theme-accent)]" />
                      ) : (
                        <OutlineStarIcon className="h-4 w-4 text-white/60" />
                      )}
                    </motion.span>
                  </button>

                  {/* Delete Button */}
                  <button
                    className="absolute top-2 left-2 z-10"
                    onClick={e => { e.stopPropagation(); confirmDeleteTrade(group.entry); }}
                    aria-label="Delete trade"
                    style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '9999px', padding: 3 }}
                  >
                    <motion.span
                      initial={{ scale: 0.8 }}
                      whileHover={{ scale: 1.1 }}
                      transition={{ type: 'spring', stiffness: 400 }}
                    >
                      <XMarkIcon className="w-4 h-4 text-white" />
                    </motion.span>
                  </button>

                  {/* Images Container */}
                  <div className="relative w-full aspect-square">
                    {group.totalImages === 1 ? (
                      // Single image - no stacking animation
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ 
                          duration: 0.4, 
                          delay: groupIdx * 0.1,
                          type: 'spring',
                          stiffness: 200
                        }}
                      >
                        <motion.img
                          src={group.firstImage.src}
                          alt="Trade Screenshot"
                          className="w-full aspect-square object-cover rounded-xl border-2 border-white/10 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-2xl"
                          whileHover={{ 
                            scale: 1.12,
                            zIndex: 50
                          }}
                          transition={{ type: 'spring', stiffness: 300 }}
                          onClick={() => {
                            openImageViewer(group.firstImage, group.entry);
                          }}
                          style={{ cursor: "pointer" }}
                        />
                      </motion.div>
                    ) : (
                      // Multiple images - stacked animation
                      <div className="relative w-full h-full">
                        {group.images.map((img, imgIdx) => (
                          <motion.div
                            key={imgIdx}
                            className="absolute inset-0"
                            initial={{ 
                              opacity: 0,
                              scale: 0.95
                            }}
                            animate={{ 
                              opacity: 1,
                              scale: 1
                            }}
                            transition={{ 
                              duration: 0.4, 
                              delay: groupIdx * 0.1 + imgIdx * 0.1,
                              type: 'spring',
                              stiffness: 200
                            }}
                            style={{
                              transform: `translate(${imgIdx * 6}px, ${imgIdx * 6}px)`,
                              zIndex: group.images.length - imgIdx
                            }}
                          >
                            <motion.img
                              src={img.src}
                              alt="Trade Screenshot"
                              className="w-full aspect-square object-cover rounded-xl border-2 border-white/10 shadow-lg"
                              whileHover={{ 
                                scale: 1.1,
                                zIndex: 50,
                                boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.4)'
                              }}
                              transition={{ type: 'spring', stiffness: 300 }}
                              onClick={() => {
                                openImageViewer(img, group.entry);
                              }}
                              style={{ cursor: "pointer" }}
                            />
                          </motion.div>
                        ))}
                        
                        {/* Image Count Badge - moved to bottom to avoid overlap with star icon */}
                        <motion.div 
                          className="absolute bottom-2 left-2 z-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full px-3 py-1 text-sm font-bold shadow-lg"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ 
                            delay: groupIdx * 0.1 + group.totalImages * 0.1 + 0.2,
                            type: 'spring',
                            stiffness: 300
                          }}
                          whileHover={{ scale: 1.1 }}
                        >
                          {group.totalImages}
                        </motion.div>
                      </div>
                    )}
                  </div>
                </>
              ) : null}

              <TradeCardMeta entry={group.entry} />
            </motion.div>
        </AnimatePresence>
        ))
        )}
      </motion.div>
      </div>

      {/* Image Viewer Modal */}
      {selectedImage && selectedImage.src && tradeEntry && (
        <ZoomableImageModal
          isOpen={!!selectedImage}
          images={viewerItems.map(item => item.src)}
          index={viewerIndex}
          onClose={closeImageViewer}
          onPrev={() => navigateViewer('prev')}
          onNext={() => navigateViewer('next')}
          showNavigation
          controlsPosition="left"
          showZoomControls={false}
          onBackgroundMouseMove={(e) => {
            const rightThreshold = window.innerWidth * 0.8;
            setShowTradeDetails(e.clientX > rightThreshold);
          }}
          onBackgroundMouseLeave={() => setShowTradeDetails(false)}
          sidePanel={
            <>
            {/* Touch/keyboard toggle: the panel otherwise only opens when the mouse nears the right edge. */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowTradeDetails((open) => !open); }}
              aria-expanded={showTradeDetails}
              aria-label={showTradeDetails ? 'Hide trade details' : 'Show trade details'}
              className={`absolute top-1/2 z-30 -translate-y-1/2 rounded-l-lg border border-r-0 border-white/20 bg-neutral-900/95 px-2 py-3 text-xs font-medium text-white transition-[right] duration-300 [writing-mode:vertical-rl] ${showTradeDetails ? 'right-80' : 'right-0'}`}
            >
              Details
            </button>
            <motion.div
              initial={{ x: 320 }}
              animate={{ x: showTradeDetails ? 0 : 320 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={e => e.stopPropagation()}
              className="absolute right-0 top-0 w-80 h-full bg-neutral-900 bg-opacity-95 backdrop-blur-md p-4 overflow-y-auto border-l border-white/20"
            >
              <h3 className="text-xl font-bold text-[#e5e5e5] mb-4">
                {tradeEntry.isDeposit ? 'Deposit Details' : 
                 tradeEntry.isPayout ? 'Payout Details' :
                 isTapeReading(tradeEntry) ? 'Tape Reading Details' : 'Trade Details'}
              </h3>
              <div className="space-y-3">
                {tradeEntry.title && (
                  <div>
                    <span className="text-sm text-[#374151]">Title:</span>
                    <div className="text-[#e5e5e5] font-semibold">{tradeEntry.title}</div>
                  </div>
                )}
                <div>
                  <span className="text-sm text-[#374151]">Date:</span>
                  <div className="text-[#e5e5e5] font-semibold">
                    {parseCreatedDate(tradeEntry).toLocaleDateString()}
                  </div>
                </div>
                {tradeEntry.accountName && (
                  <div>
                    <span className="text-sm text-[#374151]">Account:</span>
                    <div className="text-blue-400 font-semibold">{tradeEntry.accountName}</div>
                  </div>
                )}
                {tradeEntry.tickerTraded && !tradeEntry.isDeposit && !tradeEntry.isPayout && (
                  <div>
                    <span className="text-sm text-[#374151]">Ticker:</span>
                    <div className="text-[#e5e5e5] font-semibold">{tradeEntry.tickerTraded}</div>
                  </div>
                )}
                {(tradeEntry.isDeposit || tradeEntry.isPayout || (!isTapeReading(tradeEntry) && tradeEntry.pnl != null)) && (
                  <div>
                    <span className="text-sm text-[#374151]">
                      {tradeEntry.isDeposit ? 'Deposit Amount:' : 
                       tradeEntry.isPayout ? 'Payout Amount:' : 'P&L:'}
                    </span>
                    <div className={`font-semibold ${
                      tradeEntry.isDeposit ? 'text-blue-400' : 
                      tradeEntry.isPayout ? 'theme-loss' :
                      Number(tradeEntry.pnl) > 0 ? 'theme-success' :
                      Number(tradeEntry.pnl) < 0 ? 'theme-loss' : 'text-[#e5e5e5]'
                    }`}>
                      {tradeEntry.isPayout ? 
                        `$${Math.abs(Number(tradeEntry.pnl)).toFixed(2)}` : 
                        tradeEntry.isDeposit ?
                        `$${Number(tradeEntry.pnl).toFixed(2)}` :
                        `${Number(tradeEntry.pnl) > 0 ? '+' : Number(tradeEntry.pnl) < 0 ? '-' : ''}$${Math.abs(Number(tradeEntry.pnl) || 0).toFixed(2)}`
                      }
                    </div>
                  </div>
                )}
                {(tradeEntry.isDeposit || tradeEntry.isPayout) && tradeEntry.accountBalance != null && (
                  <div>
                    <span className="text-sm text-[#374151]">Account Balance:</span>
                    <div className="text-[#e5e5e5] font-semibold">${Number(tradeEntry.accountBalance).toFixed(2)}</div>
                  </div>
                )}
                {tradeEntry.rr && !tradeEntry.isDeposit && !tradeEntry.isPayout && (
                  <div>
                    <span className="text-sm text-[#374151]">R:R:</span>
                    <div className="text-[#e5e5e5] font-semibold">{tradeEntry.rr}</div>
                  </div>
                )}
                {tradeEntry.notes && (
                  <div>
                    <span className="text-sm text-[#374151]">Notes:</span>
                    <div className="text-[#e5e5e5] text-sm mt-1 whitespace-pre-wrap">{tradeEntry.notes}</div>
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    const entryId = tradeEntry.id || `${tradeEntry.created}-${tradeEntry.tickerTraded}`;
                    navigate(`/context?entryId=${encodeURIComponent(entryId)}`);
                  }}
                  className="btn-primary flex-1 hover:bg-[#2563EB] dark:hover:bg-[#3B82F6] transition-colors"
                >
                  View in Data
                </button>
                <button
                  onClick={() => confirmDeleteTrade(tradeEntry)}
                  className="btn-destructive"
                >
                  Delete
                </button>
              </div>
            </motion.div>
            </>
          }
        />
      )}

      {selectedImage && !selectedImage.src && tradeEntry && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-between z-50 overflow-hidden"
            onClick={closeImageViewer}
          >
            <button
              type="button"
              onClick={closeImageViewer}
              aria-label="Close details"
              className="absolute top-4 left-4 z-50 bg-black bg-opacity-90 text-white rounded-full p-3 hover:bg-opacity-100 hover:scale-110 transition-all duration-200 shadow-lg border border-white/20"
            >
              <XMarkIcon className="w-7 h-7" />
            </button>
            <div className="flex items-center justify-center w-full h-full relative">
              <motion.div 
                className="w-64 h-64 rounded-xl flex items-center justify-center"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
              >
                {tradeEntry.isDeposit ? (
                  <div className="text-8xl font-bold text-blue-400">$</div>
                ) : tradeEntry.isPayout ? (
                  <div className="theme-loss text-8xl font-bold">$</div>
                ) : isTapeReading(tradeEntry) ? (
                  <div className="text-8xl font-bold text-neutral-400">📊</div>
                ) : (
                  <div className="text-8xl font-bold text-neutral-400">📈</div>
                )}
              </motion.div>
            </div>

            {/* No chart to look at, so the details are the content: always shown. */}
            <motion.div
              initial={{ x: 320 }}
              animate={{ x: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={e => e.stopPropagation()}
              className="absolute right-0 top-0 w-80 max-w-full h-full bg-neutral-900 bg-opacity-95 backdrop-blur-md p-4 overflow-y-auto border-l border-white/20"
            >
              <h3 className="text-xl font-bold text-[#e5e5e5] mb-4">
                {tradeEntry.isDeposit ? 'Deposit Details' :
                 tradeEntry.isPayout ? 'Payout Details' :
                 isTapeReading(tradeEntry) ? 'Tape Reading Details' : 'Trade Details'}
              </h3>
              <div className="space-y-3">
                {tradeEntry.title && (
                  <div>
                    <span className="text-sm text-[#374151]">Title:</span>
                    <div className="text-[#e5e5e5] font-semibold">{tradeEntry.title}</div>
                  </div>
                )}
                <div>
                  <span className="text-sm text-[#374151]">Date:</span>
                  <div className="text-[#e5e5e5] font-semibold">
                    {parseCreatedDate(tradeEntry).toLocaleDateString()}
                  </div>
                </div>
                {tradeEntry.accountName && (
                  <div>
                    <span className="text-sm text-[#374151]">Account:</span>
                    <div className="text-blue-400 font-semibold">{tradeEntry.accountName}</div>
                  </div>
                )}
                {tradeEntry.tickerTraded && !tradeEntry.isDeposit && !tradeEntry.isPayout && (
                  <div>
                    <span className="text-sm text-[#374151]">Ticker:</span>
                    <div className="text-[#e5e5e5] font-semibold">{tradeEntry.tickerTraded}</div>
                  </div>
                )}
                {(tradeEntry.isDeposit || tradeEntry.isPayout || (!isTapeReading(tradeEntry) && tradeEntry.pnl != null)) && (
                  <div>
                    <span className="text-sm text-[#374151]">
                      {tradeEntry.isDeposit ? 'Deposit Amount:' :
                       tradeEntry.isPayout ? 'Payout Amount:' : 'P&L:'}
                    </span>
                    <div className={`font-semibold ${
                      tradeEntry.isDeposit ? 'text-blue-400' :
                      tradeEntry.isPayout ? 'theme-loss' :
                      Number(tradeEntry.pnl) > 0 ? 'theme-success' :
                      Number(tradeEntry.pnl) < 0 ? 'theme-loss' : 'text-[#e5e5e5]'
                    }`}>
                      {tradeEntry.isPayout ?
                        `$${Math.abs(Number(tradeEntry.pnl)).toFixed(2)}` :
                        tradeEntry.isDeposit ?
                        `$${Number(tradeEntry.pnl).toFixed(2)}` :
                        `${Number(tradeEntry.pnl) > 0 ? '+' : Number(tradeEntry.pnl) < 0 ? '-' : ''}$${Math.abs(Number(tradeEntry.pnl) || 0).toFixed(2)}`
                      }
                    </div>
                  </div>
                )}
                {(tradeEntry.isDeposit || tradeEntry.isPayout) && tradeEntry.accountBalance != null && (
                  <div>
                    <span className="text-sm text-[#374151]">Account Balance:</span>
                    <div className="text-[#e5e5e5] font-semibold">${Number(tradeEntry.accountBalance).toFixed(2)}</div>
                  </div>
                )}
                {tradeEntry.rr && !tradeEntry.isDeposit && !tradeEntry.isPayout && (
                  <div>
                    <span className="text-sm text-[#374151]">R:R:</span>
                    <div className="text-[#e5e5e5] font-semibold">{tradeEntry.rr}</div>
                  </div>
                )}
                {tradeEntry.notes && (
                  <div>
                    <span className="text-sm text-[#374151]">Notes:</span>
                    <div className="text-[#e5e5e5] text-sm mt-1 whitespace-pre-wrap">{tradeEntry.notes}</div>
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    const entryId = tradeEntry.id || `${tradeEntry.created}-${tradeEntry.tickerTraded}`;
                    navigate(`/context?entryId=${encodeURIComponent(entryId)}`);
                  }}
                  className="btn-primary flex-1 hover:bg-[#2563EB] dark:hover:bg-[#3B82F6] transition-colors"
                >
                  View in Data
                </button>
                <button
                  onClick={() => confirmDeleteTrade(tradeEntry)}
                  className="btn-destructive"
                >
                  Delete
                </button>
              </div>
              {viewerItems.length > 1 && (
                <p className="mt-4 hidden text-xs text-neutral-500 sm:block">Use ← / → to step through trades, Esc to close.</p>
              )}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
      <DayScoreCardModal
        isOpen={isDayCardOpen}
        onClose={() => setIsDayCardOpen(false)}
        dateKey={dayCardPayload?.dateKey}
        dateLabel={dayCardPayload?.dateLabel}
        pnl={dayCardPayload?.pnl}
        rr={dayCardPayload?.rr}
        percentage={dayCardPayload?.percentage}
        session={dayCardPayload?.session}
        grade={dayCardPayload?.grade}
      />
      {showDayCardToast && dayCardPayload && (
        <button
          type="button"
          onClick={() => {
            setIsDayCardOpen(true);
            setShowDayCardToast(false);
          }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[95] inline-flex items-center px-4 py-3 rounded-card border border-[#BFDBFE] dark:border-[#1E3A8A] bg-[#EFF6FF] dark:bg-[#0F172A] text-[#1D4ED8] dark:text-[#93C5FD] shadow-elevated hover:opacity-90 transition-opacity"
        >
          <span className="text-sm font-semibold">View day image</span>
        </button>
      )}
    </div>
  );
};

export default TradesPage; 
